import type { Filter } from '@renderer/types/configState'
import { imageUtils } from '@renderer/utils/imageUtils'
import { cropTo } from './useFilterThumbs'
import { displayName } from './script'

/** A 6x8 sheet at 300 dpi, portrait — what the booth's paper is. */
export const SHEET_WIDTH = 1800
export const SHEET_HEIGHT = 2400
const COLUMNS = 2
const ROWS = 4
const PAD = 30
const LABEL_HEIGHT = 70
const INK = '#7C2714'

/** Where each filter's tile goes on the sheet, in reading order. */
export function sheetTiles(count: number): { x: number; y: number; width: number; height: number }[] {
  const cellW = SHEET_WIDTH / COLUMNS
  const cellH = SHEET_HEIGHT / ROWS
  return Array.from({ length: Math.min(count, COLUMNS * ROWS) }, (_, i) => ({
    x: (i % COLUMNS) * cellW + PAD,
    y: Math.floor(i / COLUMNS) * cellH + PAD,
    width: cellW - PAD * 2,
    height: cellH - PAD * 2 - LABEL_HEIGHT
  }))
}

/**
 * The operator's filter test sheet: one camera frame through every filter on
 * the wall, two by four on a 6x8 sheet, each named underneath.
 *
 * Built from the same frame and the same `applyFilterToImage` the wall and the
 * print use, so what comes out of the printer is what each filter really does
 * on this booth's camera, light and paper — which is the only honest way to
 * judge a filter.
 */
export async function composeFilterSheet(frame: Blob, filters: Filter[]): Promise<Blob> {
  const tiles = sheetTiles(filters.length)
  const canvas = document.createElement('canvas')
  canvas.width = SHEET_WIDTH
  canvas.height = SHEET_HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no 2d context')
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, SHEET_WIDTH, SHEET_HEIGHT)

  const source = await cropTo(frame, tiles[0]?.width ?? 840, tiles[0]?.height ?? 460)
  try {
    for (let i = 0; i < tiles.length; i++) {
      const tile = tiles[i]
      const url = await imageUtils.applyFilterToImage(source, filters[i])
      try {
        const bitmap = await createImageBitmap(await (await fetch(url)).blob())
        ctx.drawImage(bitmap, tile.x, tile.y, tile.width, tile.height)
        bitmap.close()
      } finally {
        if (url !== source) URL.revokeObjectURL(url)
      }
      ctx.fillStyle = INK
      ctx.font = '600 44px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(
        `${i + 1}. ${displayName(filters[i].name)}`,
        tile.x + tile.width / 2,
        tile.y + tile.height + LABEL_HEIGHT / 2
      )
    }
  } finally {
    URL.revokeObjectURL(source)
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92)
  )
  if (!blob) throw new Error('filter sheet export failed')
  return blob
}
